import { c as _c } from "react/compiler-runtime";
import { forwardRef, type ReactNode } from "react";
import type { LucideIcon, LucideProps } from "lucide-react";
import { PROOF_BOLD_ANVIL, PROOF_BOLD_ANVIL_TRANSFORM, PROOF_BOLD_BRACKETS, PROOF_BOLD_BRACKETS_TRANSFORM, PROOF_BOLD_GRID, PROOF_BOLD_THIN_TRANSFORM, PROOF_GRID, PROOF_PIXELS } from "@repo/blacksmith-ui/anvil/proofGlyph";
function pixelIcon(name: string, grid: number, children: ReactNode, inset = 0): LucideIcon {
  const Icon = forwardRef<SVGSVGElement, LucideProps>(function PixelIcon(t0, ref) {
    const $ = _c(7);
    let props;
    let t1;
    if ($[0] !== t0) {
      ({
        size: t1,
        ...props
      } = t0);
      $[0] = t0;
      $[1] = props;
      $[2] = t1;
    } else {
      props = $[1];
      t1 = $[2];
    }
    const size = t1 === undefined ? 24 : t1;
    let t2;
    if ($[3] !== props || $[4] !== ref || $[5] !== size) {
      t2 = <svg ref={ref} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox={`${-inset} ${-inset} ${grid + 2 * inset} ${grid + 2 * inset}`} fill="currentColor" aria-hidden={true} {...props}>{children}</svg>;
      $[3] = props;
      $[4] = ref;
      $[5] = size;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  });
  Icon.displayName = name;
  return Icon;
}

/** Every surface that identifies Proof renders this glyph, never a lucide icon of its own. */
export const ProofIcon = pixelIcon("ProofIcon", PROOF_GRID, <path d={PROOF_PIXELS} />);
const PROOF_BOLD_CHILDREN = <>
    <path d={PROOF_PIXELS} transform={PROOF_BOLD_THIN_TRANSFORM} className="[@media(min-resolution:2dppx)]:hidden" />
    <g className="hidden [@media(min-resolution:2dppx)]:inline">
      <path d={PROOF_BOLD_BRACKETS} transform={PROOF_BOLD_BRACKETS_TRANSFORM} />
      <path d={PROOF_BOLD_ANVIL} transform={PROOF_BOLD_ANVIL_TRANSFORM} />
    </g>
  </>;

/**
 * For rows where Proof sits among 2/24-stroke lucide icons, e.g. the sidebar
 * nav and the settings pills. Bold strokes on 2x screens, the 1px glyph on 1x.
 * Edges land on whole pixels only at 18px (see `proofGlyph.ts`); smaller sizes
 * keep the weight but render slightly soft.
 */
export const ProofIconBold = pixelIcon("ProofIconBold", PROOF_BOLD_GRID, PROOF_BOLD_CHILDREN);

/**
 * Sits among lucide icons in tool rows; the inset matches their 2/24 padding.
 * Bold strokes at every density: inset, the 1x glyph's one cell is ~0.67px.
 */
export const ProofIconInline = pixelIcon("ProofIconInline", PROOF_BOLD_GRID, <>
    <path d={PROOF_BOLD_BRACKETS} transform={PROOF_BOLD_BRACKETS_TRANSFORM} />
    <path d={PROOF_BOLD_ANVIL} transform={PROOF_BOLD_ANVIL_TRANSFORM} />
  </>, PROOF_BOLD_GRID / 12);